import {
  FileMapping,
  ColumnMapping,
  MappingValidation,
  SmartAutoMappingResult,
  ValidationError,
  ValidationWarning,
  FormFieldTarget,
  MetadataTypeValue,
  StudyForm,
  SelectedFile,
  isFormFieldMapping,
  isMetadataMapping,
  isIgnoredMapping,
} from './types';
import {
  METADATA_PATTERNS,
} from '../constants/metadataPatterns';

interface ColumnDataAnalysis {
  isNumeric: boolean;
  isUnique: boolean;
  patterns: string[];
  avgLength: number;
  hasNulls: boolean;
}

/**
 * MappingEngine - Core business logic for CSV column mapping
 *
 * Provides pure functions for:
 * - Creating and updating file mappings
 * - Enforcing 1:1 mapping constraints
 * - Smart auto-mapping with confidence scores
 * - Comprehensive validation
 *
 * All operations are immutable - they return new objects without modifying inputs.
 */
export class MappingEngine {
  /**
   * Get data validator function for metadata type
   */
  private static getDataValidatorForMetadataType(
    type: string
  ): (analysis: ColumnDataAnalysis) => boolean {
    switch (type) {
      case 'externalSubjectId':
        return analysis =>
          !analysis.isNumeric || analysis.patterns.includes('short');
      case 'subjectNumber':
      case 'siteNumber':
        return analysis =>
          analysis.isNumeric || analysis.patterns.includes('short');
      case 'formId':
      case 'mainFormId':
        // Accept numeric IDs, short IDs, or medium-length alphanumeric IDs
        // Form IDs can be numeric (e.g., "1742") or string identifiers (e.g., "ecg_1", "ecg_data_1")
        return analysis =>
          analysis.isNumeric ||
          analysis.patterns.includes('short') ||
          (analysis.avgLength <= 20 && !analysis.isNumeric);
      case 'siteName':
      case 'subjectData':
        return analysis =>
          !analysis.isNumeric || analysis.patterns.includes('medium');
      case 'deletedForm':
      case 'trash':
        return analysis =>
          analysis.patterns.includes('boolean') ||
          analysis.patterns.includes('flag');
      case 'formIndex':
        return analysis => analysis.isNumeric;
      case 'site':
        return analysis =>
          analysis.patterns.includes('short') || analysis.isNumeric;
      default:
        return () => true;
    }
  }

  /**
   * Creates a new FileMapping for a file and form
   */
  static createMapping(fileName: string, form: StudyForm): FileMapping {
    const now = new Date();

    return {
      fileId: fileName,
      fileName,
      formId: form.id,
      formName: form.name,
      formType: form.type === 1 ? 'main' : 'subform',
      matchType: 'manual',
      columnMappings: {},
      validation: {
        isValid: false,
        errors: [],
        warnings: [],
        requiredMetadataMapped: false,
        hasFormFieldMappings: false,
        totalColumnsMapped: 0,
        unmappedColumns: [],
      },
      lastModified: now,
      createdAt: now,
    };
  }

  /**
   * Maps a column to a form field, metadata, or marks as ignored
   * Enforces 1:1 mapping constraints
   */
  static mapColumn(
    mapping: FileMapping,
    columnName: string,
    mappingType: 'formField' | 'metadata' | 'ignored',
    target?: FormFieldTarget | MetadataTypeValue
  ): FileMapping {
    // Validate input
    this.validateMappingInput(mapping, columnName, mappingType, target);

    // Create new column mapping
    const columnMapping: ColumnMapping = {
      columnName,
      mapping: this.createMappingObject(mappingType, target),
      confidence: 1.0, // Manual mapping = 100% confidence
      autoMatched: false,
      status: 'manually_mapped',
      validationErrors: [],
      lastModified: new Date(),
    };

    // Create updated mapping
    const updatedMappings = {
      ...mapping.columnMappings,
      [columnName]: columnMapping,
    };

    const updatedMapping: FileMapping = {
      ...mapping,
      columnMappings: updatedMappings,
      lastModified: new Date(),
    };

    // Validate the new mapping - throw error if invalid
    const validation = this.validateMapping(updatedMapping);
    if (!validation.isValid) {
      const duplicateError = validation.errors.find(
        e => e.type === 'duplicate_mapping'
      );
      if (duplicateError) {
        throw new Error(duplicateError.message);
      }
      // For other validation errors, just return the mapping but mark as invalid
    }

    // Update validation in the mapping
    updatedMapping.validation = validation;

    return updatedMapping;
  }

  /**
   * Smart auto-mapping: detects form and maps columns
   */
  static smartAutoMap(
    file: SelectedFile,
    availableForms: StudyForm[],
    existingMapping?: FileMapping
  ): SmartAutoMappingResult {
    const startTime = performance.now();

    // 1. Detect form if not already mapped
    const formDetection = this.detectFormFromFileName(file, availableForms);

    // 2. Create or update mapping
    const baseMapping =
      existingMapping ||
      this.createMapping(
        file.file?.name || file.csvFile?.filename || 'unknown',
        formDetection.form || availableForms[0]
      );

    // 3. Auto-map columns if form is available
    const finalMapping = formDetection.form
      ? this.autoMapColumns(
          baseMapping,
          formDetection.form,
          file.metadata.columns,
          undefined,
          file
        )
      : baseMapping;

    const processingTime = performance.now() - startTime;
    const message = this.generateAutoMappingMessage(
      formDetection,
      finalMapping
    );

    return {
      fileMapping: finalMapping,
      formDetected: !!formDetection.form,
      formConfidence: formDetection.confidence,
      columnsAutoMapped: formDetection.form ? true : false,
      totalColumnsMapped: Object.keys(finalMapping.columnMappings).length,
      message,
      processingTime,
    };
  }

  /**
   * Auto-maps columns for a given form
   */
  static autoMapColumns(
    mapping: FileMapping,
    form: StudyForm,
    availableColumns?: string[],
    sampleData?: string[][],
    file?: SelectedFile
  ): FileMapping {

    const updatedMappings = { ...mapping.columnMappings };

    // Get columns to map - either from parameter or from existing mapping keys
    const columnsToMap = availableColumns || Object.keys(updatedMappings);

    if (columnsToMap.length === 0) {
      return mapping;
    }

    // Get form field names for matching
    const formFieldNames = (form.fields || [])
      .map(field => field.dataPoint);

    // Get sample data - either from parameter or from file header data
    const effectiveSampleData =
      sampleData || file?.headerData?.first_10_rows || [];

    // Auto-classify each column
    columnsToMap.forEach((columnName, columnIndex) => {
      // Get sample data for this column if available
      const columnSampleData =
        effectiveSampleData.length > 0
          ? effectiveSampleData
              .map(row => row[columnIndex])
              .filter(val => val != null && val !== '')
          : [];
      const classification = this.autoClassifyColumn(
        columnName,
        formFieldNames,
        columnSampleData
      );

      // Skip if column is already manually mapped (don't overwrite manual mappings)
      if (
        updatedMappings[columnName] &&
        updatedMappings[columnName].status === 'manually_mapped'
      ) {
        return;
      }

      // Only create mappings for columns that actually match something (confidence > 0.3 for metadata)
      // Don't create 'ignored' mappings during auto-mapping - let them remain unmapped
      const minConfidence = classification.mappingType === 'metadata' ? 0.3 : 0;
      if (
        classification.confidence > minConfidence &&
        classification.mappingType !== 'ignored'
      ) {
        const columnMapping: ColumnMapping = {
          columnName,
          mapping: this.createMappingObject(
            classification.mappingType,
            classification.target
          ),
          confidence: classification.confidence,
          autoMatched: true,
          status: 'auto_mapped',
          validationErrors: [],
          lastModified: new Date(),
        };

        updatedMappings[columnName] = columnMapping;
      }
    });

    const finalMapping = {
      ...mapping,
      columnMappings: updatedMappings,
      validation: this.validateMapping({
        ...mapping,
        columnMappings: updatedMappings,
      }),
      lastModified: new Date(),
    };


    return finalMapping;
  }

  /**
   * Analyzes sample data in a column to understand its characteristics
   */
  private static analyzeColumnData(sampleData: string[]): {
    isNumeric: boolean;
    isUnique: boolean;
    patterns: string[];
    avgLength: number;
    hasNulls: boolean;
  } {
    if (sampleData.length === 0) {
      return {
        isNumeric: false,
        isUnique: false,
        patterns: [],
        avgLength: 0,
        hasNulls: false,
      };
    }

    const nonEmptyData = sampleData.filter(
      val => val != null && val.trim() !== ''
    );
    const uniqueValues = new Set(nonEmptyData);
    const isNumeric = nonEmptyData.every(
      val => !isNaN(Number(val)) && val.trim() !== ''
    );
    const avgLength =
      nonEmptyData.reduce((sum, val) => sum + val.length, 0) /
      nonEmptyData.length;
    const hasNulls = nonEmptyData.length < sampleData.length;

    // Simple pattern detection
    const patterns: string[] = [];
    if (isNumeric) patterns.push('numeric');
    if (uniqueValues.size === nonEmptyData.length) patterns.push('unique');
    if (avgLength < 5) patterns.push('short');
    if (hasNulls) patterns.push('nullable');

    return {
      isNumeric,
      isUnique: uniqueValues.size === nonEmptyData.length,
      patterns,
      avgLength,
      hasNulls,
    };
  }

  /**
   * Public method to classify a column for auto-mapping
   */
  static classifyColumn(
    columnName: string,
    formFields: string[],
    sampleData?: string[]
  ): {
    mappingType: 'formField' | 'metadata' | 'ignored';
    target?: FormFieldTarget | MetadataTypeValue;
    confidence: number;
  } {
    return this.autoClassifyColumn(columnName, formFields, sampleData);
  }

  /**
   * Auto-classifies a column based on its name and sample data
   */
  private static autoClassifyColumn(
    columnName: string,
    formFields: string[],
    sampleData?: string[]
  ): {
    mappingType: 'formField' | 'metadata' | 'ignored';
    target?: FormFieldTarget | MetadataTypeValue;
    confidence: number;
  } {
    const lowerColumnName = columnName.toLowerCase();

    // Analyze sample data to improve metadata detection
    const dataAnalysis = sampleData
      ? this.analyzeColumnData(sampleData)
      : ({
          isNumeric: false,
          isUnique: false,
          patterns: [],
          avgLength: 0,
          hasNulls: false,
        } as ColumnDataAnalysis);

    // FIRST: Check metadata patterns (higher priority than form fields)
    const metadataTypes: Array<{
      type: string;
      label: string;
      priority: number;
      patterns: (string | RegExp)[];
      dataValidator?: (analysis: ColumnDataAnalysis) => boolean;
    }> = METADATA_PATTERNS.map(pattern => ({
      ...pattern,
      dataValidator: MappingEngine.getDataValidatorForMetadataType(
        pattern.type
      ),
    }));

    let bestMatch = null;
    let bestConfidence = 0;
    let bestPriority = 0;

    // Check all metadata patterns
    for (const metaType of metadataTypes) {
      for (const pattern of metaType.patterns) {
        let confidence = 0;

        if (typeof pattern === 'string') {
          // String pattern
          const patternLower = pattern.toLowerCase();
          if (lowerColumnName === patternLower) {
            confidence = 1.0; // Perfect match
          } else if (
            lowerColumnName.includes(patternLower) ||
            patternLower.includes(lowerColumnName)
          ) {
            confidence = 0.8; // Partial match
          }
        } else if (pattern instanceof RegExp) {
          // Regex pattern
          if (pattern.test(lowerColumnName)) {
            confidence = 0.95; // High confidence for regex matches
          }
        }

        if (confidence > 0) {
          const weightedConfidence = confidence * (metaType.priority / 100);

          if (
            weightedConfidence > bestConfidence ||
            (weightedConfidence === bestConfidence &&
              metaType.priority > bestPriority)
          ) {
            bestMatch = metaType;
            bestConfidence = weightedConfidence;
            bestPriority = metaType.priority;
          }
        }
      }
    }

    if (bestMatch && bestConfidence >= 0.7) {
      // Validate data characteristics if validator is provided
      if (bestMatch.dataValidator && !bestMatch.dataValidator(dataAnalysis)) {
        // Data validation failed, reduce confidence significantly
        const adjustedConfidence = bestConfidence * 0.3; // Reduce confidence by 70%
        if (adjustedConfidence < 0.5) {
          // If confidence too low after adjustment, don't use this mapping
          // Continue to check form fields instead
        } else {
          return {
            mappingType: 'metadata',
            target: bestMatch.type as MetadataTypeValue,
            confidence: Math.min(adjustedConfidence, 0.95),
          };
        }
      } else {
        return {
          mappingType: 'metadata',
          target: bestMatch.type as MetadataTypeValue,
          confidence: Math.min(bestConfidence, 0.95), // Cap at 0.95 for auto-mapping
        };
      }
    }

    // SECOND: Check if it's a form field (lower priority than metadata)
    const matchingField = formFields.find(
      field => field && field.toLowerCase() === lowerColumnName
    );

    if (matchingField) {
      return {
        mappingType: 'formField',
        target: { dataPoint: matchingField } as FormFieldTarget,
        confidence: 0.9, // Auto-mapped, not manually selected
      };
    }

    return {
      mappingType: 'ignored',
      confidence: 0,
    };
  }

  /**
   * Comprehensive validation of file mapping
   */
  static validateMapping(mapping: FileMapping): MappingValidation {
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];

    // 1. Check 1:1 mapping constraints
    const oneToOneErrors = this.validateOneToOneMapping(mapping);
    errors.push(...oneToOneErrors);

    // 2. Check required metadata mapping
    const metadataErrors = this.validateRequiredMetadata(mapping);
    errors.push(...metadataErrors);

    // 3. Check form field existence
    const fieldErrors = this.validateFieldExistence(mapping);
    errors.push(...fieldErrors);

    // 4. Generate warnings
    const mappingWarnings = this.generateMappingWarnings(mapping);
    warnings.push(...mappingWarnings);

    // Calculate statistics
    const totalColumnsMapped = Object.keys(mapping.columnMappings).length;
    // unmappedColumns would need column information from file - placeholder for now
    const unmappedColumns: string[] = [];

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      requiredMetadataMapped: this.hasRequiredMetadata(mapping),
      hasFormFieldMappings: this.hasFormFieldMappings(mapping),
      totalColumnsMapped,
      unmappedColumns,
    };
  }

  /**
   * Validates 1:1 mapping constraints
   */
  private static validateOneToOneMapping(
    mapping: FileMapping
  ): ValidationError[] {
    const errors: ValidationError[] = [];
    const targetMap = new Map<string, string>();

    for (const [columnName, columnMapping] of Object.entries(
      mapping.columnMappings
    )) {
      const targetKey = this.getMappingTargetKey(columnMapping);

      if (targetMap.has(targetKey)) {
        const existingColumn = targetMap.get(targetKey)!;
        errors.push({
          type: 'duplicate_mapping',
          message: `Column "${columnName}" and "${existingColumn}" are both mapped to the same target "${targetKey}"`,
          columnName,
        });
      } else {
        targetMap.set(targetKey, columnName);
      }
    }

    return errors;
  }

  /**
   * Validates required metadata mapping
   */
  private static validateRequiredMetadata(
    mapping: FileMapping
  ): ValidationError[] {
    const errors: ValidationError[] = [];
    const requiredMetadata: MetadataTypeValue[] = [
      'externalSubjectId',
      'subjectNumber',
      'siteNumber',
    ];

    const mappedMetadata = new Set<MetadataTypeValue>();

    // Collect mapped metadata
    Object.values(mapping.columnMappings).forEach(cm => {
      if (isMetadataMapping(cm.mapping)) {
        mappedMetadata.add(cm.mapping.metadataType);
      }
    });

    // Check for missing required metadata
    requiredMetadata.forEach(metadataType => {
      if (!mappedMetadata.has(metadataType)) {
        errors.push({
          type: 'missing_required',
          message: `Required metadata "${metadataType}" is not mapped to any column`,
          fieldName: metadataType,
        });
      }
    });

    return errors;
  }

  /**
   * Validates that mapped form fields exist in the form
   */
  private static validateFieldExistence(
    _mapping: FileMapping
  ): ValidationError[] {
    const errors: ValidationError[] = [];

    // This would need form field information
    // For now, this is a placeholder that assumes fields exist
    // In real implementation, we would validate against StudyForm.fields

    return errors;
  }

  /**
   * Generates warnings for mapping issues
   */
  private static generateMappingWarnings(
    mapping: FileMapping
  ): ValidationWarning[] {
    const warnings: ValidationWarning[] = [];

    // Check for low confidence auto-mappings
    Object.values(mapping.columnMappings).forEach(cm => {
      if (cm.confidence < 0.5) {
        warnings.push({
          type: 'low_confidence',
          message: `Low confidence mapping for column "${cm.columnName}" (${Math.round(cm.confidence * 100)}%)`,
          columnName: cm.columnName,
          severity: cm.confidence < 0.3 ? 'high' : 'medium',
        });
      }
    });

    return warnings;
  }

  /**
   * Gets all form matches with their scores for a given file
   * Returns array of forms sorted by match quality (best first)
   */
  static getAllFormMatches(
    file: SelectedFile,
    forms: StudyForm[]
  ): Array<{ form: StudyForm; score: number; reason: string; matchType: 'exact' | 'substring' | 'reverse-substring' | 'word' | 'fuzzy' }> {
    const fileName = file.file?.name || file.csvFile?.filename || '';
    const fileNameWithoutExt = this.cleanFileName(fileName).toLowerCase();

    if (!fileNameWithoutExt) {
      return [];
    }

    const allMatches: Array<{
      form: StudyForm;
      score: number;
      reason: string;
      matchType: 'exact' | 'substring' | 'reverse-substring' | 'word' | 'fuzzy';
    }> = [];

    // 1. Check exact matches
    for (const form of forms) {
      const formName = form.name.toLowerCase();
      if (fileNameWithoutExt === formName) {
        allMatches.push({
          form,
          score: 1.0,
          reason: 'Exact match',
          matchType: 'exact',
        });
      }
    }

    // 2. Check substring matches (form name in file name)
    const substringMatches = this.findSubstringMatches(
      fileNameWithoutExt,
      forms
    );
    substringMatches.forEach(match => {
      // Only add if not already added as exact match
      if (!allMatches.find(m => m.form.id === match.form.id)) {
        allMatches.push({
          ...match,
          matchType: 'substring',
        });
      }
    });

    // 3. Check reverse substring matches (file name in form name)
    const reverseSubstringMatches = this.findReverseSubstringMatches(
      fileNameWithoutExt,
      forms
    );
    reverseSubstringMatches.forEach(match => {
      // Only add if not already added
      if (!allMatches.find(m => m.form.id === match.form.id)) {
        allMatches.push({
          ...match,
          matchType: 'reverse-substring',
        });
      }
    });

    // 4. Check word matches
    const wordMatches = this.findWordMatches(fileNameWithoutExt, forms);
    wordMatches.forEach(match => {
      // Only add if not already added and score is good enough
      if (
        match.score >= 0.5 &&
        !allMatches.find(m => m.form.id === match.form.id)
      ) {
        allMatches.push({
          ...match,
          matchType: 'word',
        });
      }
    });

    // 5. Add fuzzy matches for remaining forms
    for (const form of forms) {
      // Skip if already matched
      if (allMatches.find(m => m.form.id === form.id)) {
        continue;
      }

      const formName = form.name.toLowerCase();
      const similarity = this.calculateStringSimilarity(
        fileNameWithoutExt,
        formName
      );

      if (similarity > 0.3) {
        allMatches.push({
          form,
          score: similarity,
          reason: `Similarity: ${Math.round(similarity * 100)}%`,
          matchType: 'fuzzy',
        });
      }
    }

    // Sort by score (best first)
    allMatches.sort((a, b) => b.score - a.score);

    return allMatches;
  }

  /**
   * Detects form from filename
   * Uses multiple matching strategies with priority for longer substring matches
   */
  private static detectFormFromFileName(
    file: SelectedFile,
    forms: StudyForm[]
  ): { form: StudyForm | null; confidence: number; reason: string } {
    const fileName = file.file?.name || file.csvFile?.filename || '';
    const fileNameWithoutExt = this.cleanFileName(fileName).toLowerCase();

    if (!fileNameWithoutExt) {
      return {
        form: null,
        confidence: 0,
        reason: 'Empty filename',
      };
    }

    // 1. Try exact match first
    for (const form of forms) {
      const formName = form.name.toLowerCase();
      if (fileNameWithoutExt === formName) {
        return {
          form,
          confidence: 1.0,
          reason: 'Exact filename match',
        };
      }
    }

    // 2. Try substring matches (form name is substring of file name)
    // Priority: longer matches are better
    const substringMatches = this.findSubstringMatches(
      fileNameWithoutExt,
      forms
    );
    if (substringMatches.length > 0) {
      // Sort by score (longer matches have higher scores)
      substringMatches.sort((a, b) => b.score - a.score);
      const best = substringMatches[0];
      return {
        form: best.form,
        confidence: best.score,
        reason: best.reason,
      };
    }

    // 3. Try reverse substring (file name is substring of form name)
    const reverseSubstringMatches = this.findReverseSubstringMatches(
      fileNameWithoutExt,
      forms
    );
    if (reverseSubstringMatches.length > 0) {
      reverseSubstringMatches.sort((a, b) => b.score - a.score);
      const best = reverseSubstringMatches[0];
      return {
        form: best.form,
        confidence: best.score,
        reason: best.reason,
      };
    }

    // 4. Try word-based matching
    const wordMatches = this.findWordMatches(fileNameWithoutExt, forms);
    if (wordMatches.length > 0) {
      wordMatches.sort((a, b) => b.score - a.score);
      const best = wordMatches[0];
      if (best.score >= 0.5) {
        return {
          form: best.form,
          confidence: best.score,
          reason: best.reason,
        };
      }
    }

    // 5. Fallback to fuzzy matching (Levenshtein distance)
    const fuzzyMatches = forms
      .map(form => {
        const formName = form.name.toLowerCase();
        const similarity = this.calculateStringSimilarity(
          fileNameWithoutExt,
          formName
        );

        return {
          form,
          score: similarity,
          reason: `Fuzzy similarity: ${Math.round(similarity * 100)}%`,
          matchType: 'fuzzy' as const,
        };
      })
      .filter(candidate => candidate.score > 0.3)
      .sort((a, b) => b.score - a.score);

    if (fuzzyMatches.length > 0) {
      const best = fuzzyMatches[0];
      return {
        form: best.form,
        confidence: best.score,
        reason: best.reason,
      };
    }

    return {
      form: null,
      confidence: 0,
      reason: 'No suitable match found',
    };
  }

  /**
   * Finds forms whose names are substrings of the filename
   * Gives priority to longer matches
   */
  private static findSubstringMatches(
    fileName: string,
    forms: StudyForm[]
  ): Array<{ form: StudyForm; score: number; reason: string }> {
    const matches: Array<{ form: StudyForm; score: number; reason: string }> =
      [];

    for (const form of forms) {
      const formName = form.name.toLowerCase();
      if (fileName.includes(formName)) {
        // Score: 0.95 + (formLength / fileNameLength) * 0.04
        // Longer forms get higher scores
        const score = 0.95 + (formName.length / fileName.length) * 0.04;
        matches.push({
          form,
          score: Math.min(score, 0.99), // Cap at 0.99
          reason: `Form name "${form.name}" found as substring in filename`,
        });
      }
    }

    return matches;
  }

  /**
   * Finds forms whose names contain the filename as substring
   */
  private static findReverseSubstringMatches(
    fileName: string,
    forms: StudyForm[]
  ): Array<{ form: StudyForm; score: number; reason: string }> {
    const matches: Array<{ form: StudyForm; score: number; reason: string }> =
      [];

    for (const form of forms) {
      const formName = form.name.toLowerCase();
      if (formName.includes(fileName)) {
        // Score: 0.85 + (fileNameLength / formLength) * 0.05
        const score = 0.85 + (fileName.length / formName.length) * 0.05;
        matches.push({
          form,
          score: Math.min(score, 0.9), // Cap at 0.9
          reason: `Filename found as substring in form name "${form.name}"`,
        });
      }
    }

    return matches;
  }

  /**
   * Finds forms based on word matching
   * Splits by _ and - and matches words
   */
  private static findWordMatches(
    fileName: string,
    forms: StudyForm[]
  ): Array<{ form: StudyForm; score: number; reason: string }> {
    const matches: Array<{ form: StudyForm; score: number; reason: string }> =
      [];
    const fileWords = fileName.split(/[_-]/).filter(w => w.length > 0);

    for (const form of forms) {
      const formName = form.name.toLowerCase();
      const formWords = formName.split(/[_-]/).filter(w => w.length > 0);

      // Find matching words
      const matchingWords = formWords.filter(word =>
        fileWords.includes(word)
      );

      if (matchingWords.length > 0) {
        // Calculate score based on word matches and length
        const wordMatchRatio =
          matchingWords.length / Math.max(formWords.length, fileWords.length);
        const avgMatchLength =
          matchingWords.reduce((sum, w) => sum + w.length, 0) /
          matchingWords.length;
        const maxLength = Math.max(
          formName.length,
          fileName.length
        );
        const lengthRatio = avgMatchLength / maxLength;

        // Score: word match ratio * 0.7 + length ratio * 0.1
        const score = wordMatchRatio * 0.7 + lengthRatio * 0.1;

        matches.push({
          form,
          score: Math.min(score, 0.8), // Cap at 0.8
          reason: `${matchingWords.length}/${Math.max(formWords.length, fileWords.length)} words match [${matchingWords.join(', ')}]`,
        });
      }
    }

    return matches;
  }

  /**
   * Validates mapping input parameters
   */
  private static validateMappingInput(
    mapping: FileMapping,
    columnName: string,
    mappingType: 'formField' | 'metadata' | 'ignored',
    target?: FormFieldTarget | MetadataTypeValue
  ): void {
    if (!columnName || typeof columnName !== 'string') {
      throw new Error('Invalid column name');
    }

    if (mappingType === 'formField' && !target) {
      throw new Error('Form field mapping requires target field');
    }

    if (mappingType === 'metadata' && !target) {
      throw new Error('Metadata mapping requires metadata type');
    }

    // Check for existing mapping of this column
    if (mapping.columnMappings[columnName]) {
      // Allow updating existing mapping
      return;
    }
  }

  /**
   * Creates mapping object based on type
   */
  private static createMappingObject(
    mappingType: 'formField' | 'metadata' | 'ignored',
    target?: FormFieldTarget | MetadataTypeValue
  ): ColumnMapping['mapping'] {
    switch (mappingType) {
      case 'formField':
        return {
          type: 'formField',
          targetField: target as FormFieldTarget,
        };
      case 'metadata':
        return {
          type: 'metadata',
          metadataType: target as MetadataTypeValue,
        };
      case 'ignored':
        return {
          type: 'ignored',
        };
      default:
        throw new Error(`Unknown mapping type: ${mappingType}`);
    }
  }

  /**
   * Gets target key for 1:1 validation
   */
  private static getMappingTargetKey(columnMapping: ColumnMapping): string {
    const mapping = columnMapping.mapping;

    if (isFormFieldMapping(mapping)) {
      return `formField:${mapping.targetField.dataPoint}`;
    } else if (isMetadataMapping(mapping)) {
      return `metadata:${mapping.metadataType}`;
    } else if (isIgnoredMapping(mapping)) {
      return `ignored:${columnMapping.columnName}`;
    }

    return `unknown:${columnMapping.columnName}`;
  }

  /**
   * Checks if required metadata is mapped
   */
  private static hasRequiredMetadata(mapping: FileMapping): boolean {
    const required: MetadataTypeValue[] = [
      'externalSubjectId',
      'subjectNumber',
      'siteNumber',
    ];
    const mappedMetadata = new Set<MetadataTypeValue>();

    Object.values(mapping.columnMappings).forEach(cm => {
      if (isMetadataMapping(cm.mapping)) {
        mappedMetadata.add(cm.mapping.metadataType);
      }
    });

    return required.every(type => mappedMetadata.has(type));
  }

  /**
   * Checks if there are any form field mappings
   */
  private static hasFormFieldMappings(mapping: FileMapping): boolean {
    return Object.values(mapping.columnMappings).some(cm =>
      isFormFieldMapping(cm.mapping)
    );
  }

  /**
   * Generates user-friendly message for auto-mapping results
   */
  private static generateAutoMappingMessage(
    formDetection: {
      form: StudyForm | null;
      confidence: number;
      reason: string;
    },
    finalMapping: FileMapping
  ): string {
    const parts: string[] = [];

    if (formDetection.form) {
      parts.push(
        `✅ Detected form "${formDetection.form.name}" (${Math.round(formDetection.confidence * 100)}% confidence)`
      );
    } else {
      parts.push('⚠️ Could not detect form automatically');
    }

    const mappedCount = Object.keys(finalMapping.columnMappings).length;
    if (mappedCount > 0) {
      parts.push(`Auto-mapped ${mappedCount} columns`);
    }

    return parts.join('. ');
  }

  /**
   * Cleans filename for comparison - only removes extension
   * We keep the original filename structure to preserve form name patterns
   */
  private static cleanFileName(fileName: string): string {
    return fileName.replace(/\.(csv|txt|tsv)$/i, '').trim();
  }

  /**
   * Simple string similarity calculation
   */
  private static calculateStringSimilarity(a: string, b: string): number {
    const longer = a.length > b.length ? a : b;
    const shorter = a.length > b.length ? b : a;

    if (longer.length === 0) return 1.0;

    const distance = this.levenshteinDistance(longer, shorter);
    return (longer.length - distance) / longer.length;
  }

  /**
   * Levenshtein distance calculation
   */
  private static levenshteinDistance(a: string, b: string): number {
    const matrix = Array(b.length + 1)
      .fill(null)
      .map(() => Array(a.length + 1).fill(null));

    for (let i = 0; i <= a.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= b.length; j++) matrix[j][0] = j;

    for (let j = 1; j <= b.length; j++) {
      for (let i = 1; i <= a.length; i++) {
        const indicator = a[i - 1] === b[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j][i - 1] + 1, // deletion
          matrix[j - 1][i] + 1, // insertion
          matrix[j - 1][i - 1] + indicator // substitution
        );
      }
    }

    return matrix[b.length][a.length];
  }
}
